import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from "react-router-dom";
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { fetchAPI } from "@/api";
import { useWindowWidth } from '@/hooks/useWindowWidth';
import { OutletContext } from '@/types';

interface Column {
    header: string;
    width?: number;
    align?: string;
    fullWidthMobile?: boolean;
    hideHeaderMobile?: boolean;
    removePaddingRight?: boolean;
    render: (row: any, key: number) => React.ReactNode;
    noTruncate?: boolean;
    hideMobile?: boolean;
    hideDesktop?: boolean;
}

interface DataModel {
    columns: Column[];
}

interface BaseDynamicAPITableProps {
    apiMethod?: string;
    params?: any;
    dataModel: DataModel;
    pageSize?: number;
    filters?: string;
    delayLoading?: boolean;
    rowLinkTo?: (row: any) => string;
    rowLinkState?: (row: any) => any;
    preFetchedData?: any[];
    preFetchedDataLoading?: boolean;
}

export default function BaseDynamicAPITable({ apiMethod, params, dataModel, pageSize = 10, filters, delayLoading, rowLinkTo, rowLinkState, preFetchedData, preFetchedDataLoading = false }: BaseDynamicAPITableProps) {
    const windowWidth = useWindowWidth();
    const { auth, project } = useOutletContext<OutletContext>();
    const navigate = useNavigate();

    const [rows, setRows] = useState<any[]>(preFetchedData || []);
    const [pageNumber, setPageNumber] = useState(0);
    const [count, setCount] = useState(preFetchedData ? preFetchedData.length : 0);
    const [isLoading, setIsLoading] = useState(preFetchedDataLoading);
    const [error, setError] = useState<string | null>(null);

    if (!apiMethod && !preFetchedData) throw new Error("API method or pre-fetched data must be provided");
    if (!dataModel) throw new Error("Data model not set");

    const fetchData = useCallback(async (controller: AbortController) => {
        if (preFetchedData) return;

        try {
            let filtersMode = undefined;
            let filtersParam = undefined;

            setIsLoading(true);

            if (filters) {
                const data = JSON.parse(filters);

                if (data.filters) {
                    filtersParam = data.filters;
                }
                if (data.filtersMode) {
                    filtersMode = data.filtersMode;
                }
            }

            const response = await fetchAPI(apiMethod!, auth.user.token, {
                projectId: project.projectId,
                filters: filtersParam,
                filtersMode,
                limit: typeof pageSize === "undefined" ? 20 : pageSize,
                offset: pageNumber * pageSize,
                ...params,
            }, { signal: controller.signal });
            if (!response.ok) {
                throw new Error(`This is an HTTP error: The status is ${response.status}`);
            }
            let actualData = await response.json();
            // console.log('actualData', actualData);
            setRows(actualData.rows);
            setCount(actualData.count);
            setError(null);
        } catch (error: any) {
            let errorMessage = 'An unexpected error occurred';
            if (error.response?.message && error.name !== "AbortError") {
                errorMessage = error.response.message;
            }
            setError(errorMessage);
        } finally {
            if (!controller.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [apiMethod, auth.user.token, filters, pageNumber, pageSize, project.projectId, preFetchedData]);

    useEffect(() => {
        setIsLoading(preFetchedDataLoading);
    }, [preFetchedDataLoading]);

    useEffect(() => {
        setRows(preFetchedData || []);
        setCount(preFetchedData ? preFetchedData.length : 0);
    }, [preFetchedData]);

    useEffect(() => {
        if (delayLoading || preFetchedData) return;
        const controller = new AbortController();
        fetchData(controller);

        return () => {
            controller.abort();
        };
    }, [fetchData, pageNumber, filters, delayLoading, preFetchedData]);

    const fixedWidthColumns = dataModel.columns.filter(column => typeof column.width === 'number');
    const fixedWidth = fixedWidthColumns.reduce((acc, column) => acc + (column.width || 0), 0);
    const hideDesktopCount = dataModel.columns.filter(column => column.hideDesktop).length;
    const hideMobileCount = dataModel.columns.filter(column => column.hideMobile).length;
    const countHiddenColumns = Math.min(hideDesktopCount, hideMobileCount);
    const flexibleColumnsCount = dataModel.columns.length - countHiddenColumns - fixedWidthColumns.length;
    const flexibleWidth = flexibleColumnsCount > 0 ? `calc((100% - ${fixedWidth}%) / ${flexibleColumnsCount})` : 'auto';

    const renderRow = (row: any, key: number, rowsLength: number) => {
        const rowInner = (column: Column, i: number) => (
          <div
            className={classNames(
                  'lg:flex lg:items-center lg:flex-1 lg:border-b lg:border-b-[#F2F2F2] lg:first:border-l lg:first:border-l-[#F2F2F2] lg:first:pl-6 lg:first:pr-0 lg:last:pr-6 lg:last:border-r lg:last:border-r-[#F2F2F2] lg:py-4 lg:min-h-[64px] whitespace-nowrap lg:max-w-none',
              (!column.fullWidthMobile && i > 1) && 'w-1/2 lg:w-auto',
              column.fullWidthMobile ? 'w-full max-w-full' : 'max-w-[calc(50%-12px)]',
              rowsLength - 1 === key ? 'first:rounded-b-md last:rounded-b-md' : '',
              column.header.toLowerCase() === 'actions' && (windowWidth < 1024) && 'absolute top-3 right-3 w-auto',
              column.removePaddingRight ? 'lg:pl-2' : 'lg:px-2',
            )}
            style={{
                maxWidth: windowWidth >= 1024 ? (column.width ? `${column.width}%` : flexibleWidth) : 'auto',
                ...( ((windowWidth < 1024 && column.hideMobile) || (windowWidth >= 1024 && column.hideDesktop)) && { display: 'none' } ),
            }}
            key={`table-row-inner-${uuidv4()}`}
          >
              {!column.hideHeaderMobile && <div className="block lg:hidden text-left text-[11px]/[1.1] text-[#A5A5A5] mb-1">{column.header}</div>}
              <div
                className={classNames(
                  !column.noTruncate && 'truncate w-full',
                )}
                style={{ textAlign: (windowWidth >= 1024 ? column.align : 'left') as CSSProperties['textAlign'], }}
              >{column.render(row, key)}</div>
          </div>
        );

        return (
          <div
            className={classNames(
              'flex flex-col lg:flex-row gap-4 lg:gap-0 px-4 pt-4 pb-5 lg:p-0 rounded-[10px] lg:rounded-none bg-white lg:hover:bg-[#FAFAFA] text-sm transition relative last:rounded-bl-md last:rounded-br-md',
              rowLinkTo ? 'cursor-pointer' : '',
            )}
            key={`table-row-${uuidv4()}`}
            onClick={(event) => {
                const target = event.target as HTMLElement;
                if (target.tagName.toLowerCase() === 'a' || target.tagName.toLowerCase() === 'button') return;

                if (rowLinkTo) {
                    navigate(rowLinkTo(row), { state: rowLinkState ? rowLinkState(row) : null });
                }
            }}
          >
              {windowWidth >= 1024
                ? dataModel.columns.map((column, i) => rowInner(column, i))
                : dataModel.columns.map((column, i) =>
                  i % 2 === 0
                    ? (
                      <div className={classNames(
                        'flex w-full flex-wrap lg:gap-0',
                        column.fullWidthMobile ? 'gap-3' : (i === 0 ? 'gap-1' : 'gap-6'),
                      )} key={`table-row-column-${uuidv4()}`}>
                          {rowInner(dataModel.columns[i], i)}
                          {i + 1 < dataModel.columns.length - countHiddenColumns && rowInner(dataModel.columns[i + 1], i + 1)}
                      </div>
                    )
                    : null
                )
              }
          </div>
        );
    };

    const renderPlaceholderRow = () => {
        const rowInner = (column: Column, i: number) => (
          <div
            className={classNames(
              'lg:flex-1 lg:px-2 lg:border-b lg:border-b-[#F2F2F2] lg:first:border-l lg:first:border-l-[#F2F2F2] lg:first:pl-6 lg:first:pr-0 lg:last:pr-6 lg:last:border-r lg:last:border-r-[#F2F2F2] lg:py-4 lg:min-h-[64px] whitespace-nowrap max-w-[calc(50%-12px)] lg:max-w-none',
              (!column.fullWidthMobile && i > 1) && 'w-1/2 lg:w-auto',
              column.fullWidthMobile ? 'w-full max-w-full' : 'max-w-[calc(50%-12px)]',
            )}
            style={{
                maxWidth: windowWidth >= 1024 ? (column.width ? `${column.width}%` : flexibleWidth) : 'auto',
                textAlign: (windowWidth >= 1024 ? column.align : 'left') as CSSProperties['textAlign'],
            }}
            key={`table-placeholder-row-inner-${uuidv4()}`}
          >
              {!column.hideHeaderMobile &&
                <div className="block lg:hidden text-left text-[11px]/[1.1] text-[#A5A5A5] mb-1">{column.header}</div>}
              <div className="h-[18.2px] sm:mt-1.5">
                  <div className="animate-pulse bg-[#F2F2F2] rounded-full w-1/2 h-4 sm:h-3 inline-block"></div>
              </div>
          </div>
        );

        return (
          <div key={`placeholder-row-${uuidv4()}`} className="flex flex-col lg:flex-row gap-4 lg:gap-0 px-4 pt-4 pb-5 lg:p-0 rounded-[10px] lg:rounded-none bg-white lg:hover:bg-[#FAFAFA] text-sm transition last:rounded-bl-md last:rounded-br-md">
              {windowWidth >= 1024
                ? dataModel.columns.map((column, i) => rowInner(column, i))
                : dataModel.columns.map((column, i) =>
                  i % 2 === 0
                    ? (
                      <div className={classNames(
                        'flex w-full flex-wrap lg:gap-0',
                        column.fullWidthMobile ? 'max-w-[50%] gap-3' : (i === 0 ? 'gap-1' : 'gap-6'),
                      )} key={`table-placeholder-row-column-${uuidv4()}`}>
                          {rowInner(dataModel.columns[i], i)}
                          {i + 1 < dataModel.columns.length - countHiddenColumns && rowInner(dataModel.columns[i + 1], i + 1)}
                      </div>
                    )
                    : null
                )
              }
          </div>
        )
    }

    let rowElements;

    if (isLoading) {
        rowElements = [];
        let renderCount = pageSize;
        if ( count > 0 && (pageNumber + 1) * pageSize > count ) {
            renderCount = count - pageNumber * pageSize;
        }
        for ( let i = 0; i < renderCount; i++ ) {
            rowElements.push(renderPlaceholderRow());
        }
    } else {
        rowElements = rows.map((row, i) => renderRow(row, i, rows.length));
    }

    let totalPages = Math.ceil(count / pageSize);

    let isPrevDisabled = pageNumber === 0;
    let isNextDisabled = pageNumber === totalPages - 1;

    return (
      <div>
          {(isLoading || count > 0) &&
            <div className="flex flex-col table-auto w-full rounded-md gap-4 lg:gap-0">
                <div className="hidden lg:flex flex-row text-sm font-medium text-[#A5A5A5] bg-[#FAFAFA] rounded-tl-md rounded-tr-md">
                    {dataModel.columns.map((column: Column) =>
                        <div
                            className="flex-1 border-y border-y-[#F2F2F2] px-2 first:pl-6 last:pr-5 first:border-l first:border-l-[#F2F2F2] first:rounded-tl-md last:border-r last:border-r-[#F2F2F2] last:rounded-tr-md py-[11px] whitespace-nowrap"
                            style={{
                                maxWidth: windowWidth >= 1024 ? (column.width ? `${column.width}%` : flexibleWidth) : 'auto',
                                ...( ((windowWidth < 1024 && column.hideMobile) || (windowWidth >= 1024 && column.hideDesktop)) && { display: 'none' } ),
                            }}
                            key={`column-header-${uuidv4()}`}>
                              {/*<div className={"text-" + column.align}>{column.header}</div>*/}
                              <div>{column.header}</div>
                        </div>
                    )}
                </div>
                {rowElements}
            </div>
          }

          {!isLoading && count === 0 &&
            <div className="rounded-md custom-dash text-sm/[1.3] font-medium text-center p-8">
                No records
            </div>
          }

          {/*{!isLoading && error &&*/}
          {/*  <div className="rounded-md custom-dash text-sm/[1.3] font-medium text-center p-8">*/}
          {/*      {error}*/}
          {/*  </div>*/}
          {/*}*/}

          {count > 0 &&
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-6 sm:mt-8 sm:min-h-8">
                <div className="text-[11px]/[1.1] sm:text-sm/[1.3] text-[#A5A5A5] font-medium text-center sm:text-left mb-8 sm:mb-0">
                    Showing <span>{pageNumber * pageSize + 1}</span> to <span>{Math.min(count, (pageNumber + 1) * pageSize)}</span> of <span
                    className="font-medium text-brand-green">{count}</span> results
                </div>
                {(!isLoading && count > pageSize) &&
                    <nav className="mb-4 sm:mb-0" role="navigation" aria-label="Navigation">
                        <ul className="flex justify-center flex-col-reverse sm:flex-row gap-2.5 sm:gap-2">
                            <li>
                                <button className={isPrevDisabled ? "btn-secondary-inverted cursor-not-allowed" : "btn-secondary"}
                                   onClick={() => (pageNumber > 0 ? !isPrevDisabled && setPageNumber(pageNumber - 1) : {})}>Prev</button>
                            </li>
                            <li>
                                <button className={isNextDisabled ? "btn-secondary-inverted cursor-not-allowed" : "btn-secondary"}
                                   onClick={() => (pageNumber < totalPages - 1 ? !isNextDisabled && setPageNumber(pageNumber + 1) : {})}>Next</button>
                            </li>
                        </ul>
                    </nav>
                }
            </div>
          }
        </div>
    );
}
